import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi as defaultAdminApi } from './admin-api';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { formatTime } from '../lib/format';
import { StatusBadge } from '../components/StatusBadge';
import './admin.css';

const REFRESH_MS = 60000;
const SOURCE_LABELS = { kiosk: 'Tablet', app: 'App' };

// Ingresos de un día para recepción: quién entró, a qué hora, por dónde y el
// estado de su cuota HOY (lo calcula la API; la página no deriva nada). Sin
// fecha elegida, la API usa el día de hoy en hora argentina y la devuelve.
// Se refresca sola cada minuto sin vaciar la lista.
export function CheckinsPage({ api = defaultAdminApi }) {
  const { handleApiError } = useAuth();
  const [state, setState] = useState({ status: 'loading', checkins: [], message: '' });
  const [date, setDate] = useState('');
  const dateRef = useRef('');
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async ({ background = false } = {}) => {
    const requested = dateRef.current;
    if (!background) setState((previous) => ({ ...previous, status: 'loading', message: '' }));
    try {
      const result = await api.listCheckins(requested ? { date: requested } : {});
      if (!mounted.current || dateRef.current !== requested) return;
      if (!requested && result?.date) {
        dateRef.current = result.date;
        setDate(result.date);
      }
      setState({ status: 'ready', checkins: result?.checkins || [], message: '' });
    } catch (error) {
      if (!mounted.current || dateRef.current !== requested) return;
      const outcome = handleApiError(error);
      // Un refresco automático que falla no borra lo que ya se ve.
      if (!outcome.handled && !background) setState({ status: 'error', checkins: [], message: errorMessageFor(error) });
    }
  }, [api, handleApiError]);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load({ background: true }), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  function changeDate(value) {
    if (!value || value === dateRef.current) return;
    dateRef.current = value;
    setDate(value);
    load();
  }

  const count = state.checkins.length;

  return (
    <section className="gym-page gym-admin-checkins" aria-labelledby="gym-page-title">
      <header className="gym-page__header">
        <div>
          <h1 id="gym-page-title" className="gym-page__title">Ingresos</h1>
          <p className="gym-admin-checkins__intro">Quién entró al gimnasio y cómo está su cuota. Se actualiza solo cada minuto.</p>
        </div>
      </header>

      <div className="gym-admin-checkins__filters">
        <label className="gym-field">
          <span>Fecha</span>
          <input className="gym-input" type="date" value={date} onChange={(event) => changeDate(event.target.value)} />
        </label>
        {state.status === 'ready' && <p className="gym-admin-checkins__count">{count} {count === 1 ? 'ingreso' : 'ingresos'}</p>}
      </div>

      {state.status === 'loading' && <div role="status" className="gym-state gym-state--loading"><p className="gym-state__text">Cargando ingresos…</p></div>}
      {state.status === 'error' && (
        <div role="alert" className="gym-state gym-state--error">
          <p className="gym-state__text">{state.message}</p>
          <button type="button" className="gym-button gym-button--secondary" onClick={() => load()}>Reintentar</button>
        </div>
      )}
      {state.status === 'ready' && count === 0 && (
        <div className="gym-state gym-state--empty"><p className="gym-state__text">No hubo ingresos este día.</p></div>
      )}
      {state.status === 'ready' && count > 0 && (
        <table className="gym-table gym-admin-checkins-table">
          <caption className="gym-sr-only">Ingresos del día</caption>
          <thead>
            <tr>
              <th scope="col">Hora</th>
              <th scope="col">Socio</th>
              <th scope="col">Ingresó por</th>
              <th scope="col">Cuota hoy</th>
            </tr>
          </thead>
          <tbody>
            {state.checkins.map((checkin) => (
              <tr key={checkin.id}>
                <td data-label="Hora">{formatTime(checkin.checkedInAt)}</td>
                <td data-label="Socio"><Link to={`/admin/socios/${checkin.memberId}`}>{checkin.memberName || 'Socio'}</Link></td>
                <td data-label="Ingresó por">{SOURCE_LABELS[checkin.source] || checkin.source}</td>
                <td data-label="Cuota hoy"><StatusBadge status={checkin.membershipStatus} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
